import * as Sentry from '@sentry/react';
import { ApiError } from './ApiError';

// 401 is already handled by the silent-refresh/redirect flow in iaRequest/authRequest, so it
// carries no new information for on-call. The rest of these are expected client-input failures
// (bad request, forbidden, not found, conflict, validation) rather than app bugs — worth keeping
// in Sentry for pattern-spotting, but not at 'error' level, which is what alert rules page on.
const EXPECTED_STATUSES_SILENT = [401];
// 429 (rate limited) is the backend doing its job, not ours failing — same "expected, not a
// bug" bucket as the validation/conflict statuses below.
const EXPECTED_STATUSES_WARNING = [400, 403, 404, 409, 422, 429];

interface ReportErrorOptions {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

// Central Sentry.captureException wrapper — classifies ApiError failures by HTTP status so
// alert rules scoped to level:error/fatal only page on genuine app bugs, not expected
// client-input failures or already-handled auth expiry.
export const reportError = (error: unknown, { tags, extra }: ReportErrorOptions = {}) => {
  const status = error instanceof ApiError ? error.status : null;
  if (status !== null && EXPECTED_STATUSES_SILENT.includes(status)) return;

  const level = status !== null && EXPECTED_STATUSES_WARNING.includes(status) ? 'warning' : 'error';
  Sentry.captureException(error, {
    level,
    tags,
    extra: status !== null ? { ...extra, status } : extra,
  });
};
